import React, { useState, useEffect } from 'react'
import { Modal, LoadingOverlay, Stack, Card, Grid, Text, TextInput, Select, NumberInput, Switch, Flex, Button, ActionIcon } from '@mantine/core'
import { IconPlus, IconTrash } from '@tabler/icons-react'
import { getAllCityList, getAllProvinceList, getAllCountryList } from '../../../../services/location'
import { getCouriers } from '../../../../services/global-settings'
import { CURRENCIES } from '../../../../helper/master-unit'

const ModalEditRate = ({
    opened,
    onClose,
    loading,
    isEditing,
    formData,
    setFormData,
    sourceCountry,
    destCountry,
    onSave
}) => {
    // Courier options
    const [courierOptions, setCourierOptions] = useState([])
    const [courierLoading, setCourierLoading] = useState(false)

    // Origin location options
    const [originOptions, setOriginOptions] = useState([])
    const [originLoading, setOriginLoading] = useState(false)

    // Destination location options
    const [destinationOptions, setDestinationOptions] = useState([])
    const [destinationLoading, setDestinationLoading] = useState(false)

    // Fetch couriers
    useEffect(() => {
        const fetchCouriers = async () => {
            setCourierLoading(true)
            try {
                const response = await getCouriers({ 'page[num]': 1, 'page[limit]': 1000 })
                if (response.success && response.data) {
                    const options = response.data.map(item => ({
                        value: String(item.id),
                        label: item.attributes.name
                    }))
                    setCourierOptions(options)
                }
            } catch (error) {
                console.error('Failed to fetch couriers', error)
            } finally {
                setCourierLoading(false)
            }
        }

        if (opened) {
            fetchCouriers()
        }
    }, [opened])

    // Fetch origin options when origin_type changes
    useEffect(() => {
        const fetchOriginOptions = async () => {
            if (!sourceCountry?.id) return

            setOriginLoading(true)
            try {
                let response
                if (formData.origin_type === 'cities') {
                    response = await getAllCityList({
                        'filter[country_id][is]': sourceCountry.id,
                        'page[limit]': 1000,
                        'sort': 'name'
                    })
                } else if (formData.origin_type === 'province') {
                    response = await getAllProvinceList({
                        'filter[country_id][is]': sourceCountry.id,
                        'page[limit]': 1000,
                        'sort': 'name'
                    })
                }

                if (response?.success && response.data) {
                    const options = response.data.map(item => ({
                        value: String(item.id),
                        label: item.attributes?.name || item.name
                    }))
                    setOriginOptions(options)
                }
            } catch (error) {
                console.error('Failed to fetch origin options', error)
                setOriginOptions([])
            } finally {
                setOriginLoading(false)
            }
        }

        if (opened && formData.origin_type) {
            fetchOriginOptions()
        }
    }, [opened, formData.origin_type, sourceCountry?.id])

    // Fetch destination options when destination_type changes
    useEffect(() => {
        const fetchDestinationOptions = async () => {
            if (!destCountry?.id) return

            setDestinationLoading(true)
            try {
                let response
                if (formData.destination_type === 'Country') {
                    // For country, just use the destination country itself
                    setDestinationOptions([{
                        value: String(destCountry.id),
                        label: destCountry.attributes?.name || destCountry.name
                    }])
                    setDestinationLoading(false)
                    return
                } else if (formData.destination_type === 'province') {
                    response = await getAllProvinceList({
                        'filter[country_id][is]': destCountry.id,
                        'page[limit]': 1000,
                        'sort': 'name'
                    })
                } else if (formData.destination_type === 'cities') {
                    response = await getAllCityList({
                        'filter[country_id][is]': destCountry.id,
                        'page[limit]': 1000,
                        'sort': 'name'
                    })
                }

                if (response?.success && response.data) {
                    const options = response.data.map(item => ({
                        value: String(item.id),
                        label: item.attributes?.name || item.name
                    }))
                    setDestinationOptions(options)
                }
            } catch (error) {
                console.error('Failed to fetch destination options', error)
                setDestinationOptions([])
            } finally {
                setDestinationLoading(false)
            }
        }

        if (opened && formData.destination_type) {
            fetchDestinationOptions()
        }
    }, [opened, formData.destination_type, destCountry?.id])

    // Handle origin type change - reset origin selection
    const handleOriginTypeChange = (val) => {
        setFormData(prev => ({
            ...prev,
            origin_type: val,
            origin_id: null,
            origin_name: ''
        }))
    }

    // Handle origin selection
    const handleOriginChange = (val) => {
        const selected = originOptions.find(opt => opt.value === val)
        setFormData(prev => ({
            ...prev,
            origin_id: val ? parseInt(val) : null,
            origin_name: selected?.label || ''
        }))
    }

    // Handle destination type change - reset destination selection
    const handleDestinationTypeChange = (val) => {
        setFormData(prev => ({
            ...prev,
            destination_type: val,
            destination_id: null,
            destination_name: ''
        }))
    }

    // Handle destination selection
    const handleDestinationChange = (val) => {
        const selected = destinationOptions.find(opt => opt.value === val)
        setFormData(prev => ({
            ...prev,
            destination_id: val ? parseInt(val) : null,
            destination_name: selected?.label || ''
        }))
    }

    // Handle advance rate add
    const handleAddAdvanceRate = () => {
        setFormData(prev => ({
            ...prev,
            advance_rates: [...prev.advance_rates, { rate_per_weight: '', rate: '' }]
        }))
    }

    // Handle advance rate remove
    const handleRemoveAdvanceRate = (index) => {
        setFormData(prev => ({
            ...prev,
            advance_rates: prev.advance_rates.filter((_, i) => i !== index)
        }))
    }

    // Handle advance rate change
    const handleAdvanceRateChange = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            advance_rates: prev.advance_rates.map((item, i) =>
                i === index ? { ...item, [field]: value } : item
            )
        }))
    }

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={<Text fw={700} size="lg">{isEditing ? 'Edit Logistic Rate' : 'Add Logistic Rate'}</Text>}
            size="xl"
        >
            <LoadingOverlay visible={loading} />
            <Stack gap="md">
                {/* Row 1: Shipping Type & Method */}
                <Card withBorder p="md" radius="md">
                    <Grid>
                        <Grid.Col span={6}>
                            <TextInput
                                label="Shipping Type"
                                value={formData.shipping_types}
                                disabled
                                styles={{ input: { backgroundColor: '#f1f3f5' } }}
                            />
                        </Grid.Col>
                        <Grid.Col span={6}>
                            <Select
                                label="Shipping Method"
                                data={[{ value: 'courier', label: 'courier' }, { value: 'cargo', label: 'cargo' }]}
                                value={formData.shipping_method}
                                onChange={(val) => setFormData(prev => ({ ...prev, shipping_method: val }))}
                            />
                        </Grid.Col>
                    </Grid>
                </Card>

                {/* Row 2: Origin & Destination */}
                <Card withBorder p="md" radius="md">
                    <Grid>
                        {/* Origin Section */}
                        <Grid.Col span={6}>
                            <Grid>
                                <Grid.Col span={6}>
                                    <TextInput
                                        label="Origin Country"
                                        value={sourceCountry?.attributes?.name || ''}
                                        disabled
                                        styles={{ input: { backgroundColor: '#f1f3f5' } }}
                                    />
                                </Grid.Col>
                                <Grid.Col span={6}>
                                    <Select
                                        label="Origin Method"
                                        data={[
                                            { value: 'cities', label: 'Cities' },
                                            { value: 'province', label: 'Province' }
                                        ]}
                                        value={formData.origin_type}
                                        onChange={handleOriginTypeChange}
                                    />
                                </Grid.Col>
                                <Grid.Col span={12}>
                                    <Select
                                        label="Origin Name"
                                        data={originOptions}
                                        value={formData.origin_id ? String(formData.origin_id) : null}
                                        onChange={handleOriginChange}
                                        placeholder={originLoading ? 'Loading...' : 'Select origin'}
                                        searchable
                                        clearable
                                        disabled={originLoading}
                                        nothingFoundMessage="No options found"
                                    />
                                </Grid.Col>
                            </Grid>
                        </Grid.Col>

                        {/* Destination Section */}
                        <Grid.Col span={6}>
                            <Grid>
                                <Grid.Col span={6}>
                                    <TextInput
                                        label="Destination Country"
                                        value={destCountry?.attributes?.name || ''}
                                        disabled
                                        styles={{ input: { backgroundColor: '#f1f3f5' } }}
                                    />
                                </Grid.Col>
                                <Grid.Col span={6}>
                                    <Select
                                        label="Destination Method"
                                        data={[
                                            { value: 'Country', label: 'Country' },
                                            { value: 'province', label: 'Province' },
                                            { value: 'cities', label: 'Cities' }
                                        ]}
                                        value={formData.destination_type}
                                        onChange={handleDestinationTypeChange}
                                    />
                                </Grid.Col>
                                <Grid.Col span={12}>
                                    <Select
                                        label="Destination Name"
                                        data={destinationOptions}
                                        value={formData.destination_id ? String(formData.destination_id) : null}
                                        onChange={handleDestinationChange}
                                        placeholder={destinationLoading ? 'Loading...' : 'Select destination'}
                                        searchable
                                        clearable
                                        disabled={destinationLoading || formData.destination_type === 'Country'}
                                        nothingFoundMessage="No options found"
                                    />
                                </Grid.Col>
                            </Grid>
                        </Grid.Col>
                    </Grid>
                </Card>

                {/* Row 3: Calculation, Currency, Unit, Courier */}
                <Card withBorder p="md" radius="md">
                    <Grid>
                        <Grid.Col span={6}>
                            <Select
                                label="Calculation"
                                data={[
                                    { value: 'flat', label: 'flat' },
                                    { value: 'progressive', label: 'progressive' }
                                ]}
                                value={formData.calculation}
                                onChange={(val) => setFormData(prev => ({ ...prev, calculation: val }))}
                            />
                        </Grid.Col>
                        <Grid.Col span={6}>
                            <Select
                                label="Currency"
                                data={CURRENCIES}
                                value={formData.currency}
                                onChange={(val) => setFormData(prev => ({ ...prev, currency: val }))}
                            />
                        </Grid.Col>
                        <Grid.Col span={6}>
                            <Select
                                label="Unit"
                                data={[
                                    { value: 'kg', label: 'kg' },
                                    { value: 'lb', label: 'lb' }
                                ]}
                                value={formData.weight_unit}
                                onChange={(val) => setFormData(prev => ({ ...prev, weight_unit: val }))}
                            />
                        </Grid.Col>
                        <Grid.Col span={6}>
                            <Select
                                label="Courier Name"
                                data={courierOptions}
                                value={formData.courier_id ? String(formData.courier_id) : null}
                                onChange={(val) => setFormData(prev => ({ ...prev, courier_id: val }))}
                                placeholder={courierLoading ? 'Loading...' : 'Select courier'}
                                searchable
                                clearable
                                disabled={courierLoading}
                            />
                        </Grid.Col>
                    </Grid>
                </Card>

                {/* Row 4: Min, Max, Insurance, Remark, Name */}
                <Card withBorder p="md" radius="md">
                    <Grid>
                        <Grid.Col span={8}>
                            <Grid>
                                <Grid.Col span={6}>
                                    <NumberInput
                                        label="Minimum"
                                        value={formData.minimum}
                                        onChange={(val) => setFormData(prev => ({ ...prev, minimum: val }))}
                                    />
                                </Grid.Col>
                                <Grid.Col span={6}>
                                    <NumberInput
                                        label="Maximum"
                                        value={formData.maximum}
                                        onChange={(val) => setFormData(prev => ({ ...prev, maximum: val }))}
                                    />
                                </Grid.Col>
                                <Grid.Col span={6}>
                                    <TextInput
                                        label="Remark"
                                        value={formData.remarks}
                                        onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
                                    />
                                </Grid.Col>
                                <Grid.Col span={6}>
                                    <TextInput
                                        label="Rate Name"
                                        value={formData.name}
                                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                    />
                                </Grid.Col>
                            </Grid>
                        </Grid.Col>
                        <Grid.Col span={4}>
                            <Stack gap="xs">
                                <Text size="sm" fw={500}>Insurance</Text>
                                <Switch
                                    checked={formData.insurance}
                                    onChange={(e) => setFormData(prev => ({ ...prev, insurance: e.currentTarget.checked }))}
                                    color="teal"
                                />
                                <NumberInput
                                    label="Insurance (%)"
                                    value={formData.insurance_percentage}
                                    onChange={(val) => setFormData(prev => ({ ...prev, insurance_percentage: val }))}
                                    disabled={!formData.insurance}
                                />
                            </Stack>
                        </Grid.Col>
                    </Grid>
                </Card>

                {/* Row 5: Advance Rate Details */}
                <Card withBorder p="md" radius="md">
                    <Flex justify="space-between" align="center" mb="md">
                        <Text fw={600}>Advance Rate Details</Text>
                        <ActionIcon variant="light" onClick={handleAddAdvanceRate}>
                            <IconPlus size={16} />
                        </ActionIcon>
                    </Flex>
                    <Stack gap="xs">
                        {formData.advance_rates.map((item, index) => (
                            <Grid key={index} align="flex-end">
                                <Grid.Col span={5}>
                                    <NumberInput
                                        label={`Weight (${formData.weight_unit})`}
                                        value={item.rate_per_weight}
                                        onChange={(val) => handleAdvanceRateChange(index, 'rate_per_weight', val)}
                                    />
                                </Grid.Col>
                                <Grid.Col span={5}>
                                    <NumberInput
                                        label={`Rate (${formData.currency})`}
                                        value={item.rate}
                                        onChange={(val) => handleAdvanceRateChange(index, 'rate', val)}
                                    />
                                </Grid.Col>
                                <Grid.Col span={2}>
                                    <ActionIcon
                                        variant="subtle"
                                        color="red"
                                        onClick={() => handleRemoveAdvanceRate(index)}
                                        disabled={formData.advance_rates.length <= 1}
                                    >
                                        <IconTrash size={16} />
                                    </ActionIcon>
                                </Grid.Col>
                            </Grid>
                        ))}
                    </Stack>
                </Card>

                {/* Action Buttons */}
                <Flex justify="flex-end" gap="md">
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button color="red" onClick={onSave}>Save</Button>
                </Flex>
            </Stack>
        </Modal>
    )
}

export default ModalEditRate
